import { reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import { intersection as $intersection } from 'lodash-es'
import { useUserProfileStore } from '@/ContextTab/stores/userProfile'
import { useCodesStore } from '@/ContextTab/stores/codes/codes'
import { useNewsStore } from '@/ContextTab/stores/news'
import type { TCodeId, TNewsId } from '@/ContextTab/stores/types'


export const useColorsStore = defineStore('colors', () => {

  const codesStore = useCodesStore()
  const newsStore = useNewsStore()
  const userProfileStore = useUserProfileStore()

  const newsColoredCodes = reactive<Record<TNewsId, TCodeId[]>>({})
  const allCodes = computed(() => codesStore.allCodes)


  function recursivelyAddChildren(codes: TCodeId[], curr: TCodeId) {
    if (allCodes.value[curr]) {
      allCodes.value[curr].children.forEach((child) => {
        codes.push(child)
        recursivelyAddChildren(codes, child)
      })
    }
  }

  const userCodeColors = computed(() => (userProfileStore.profile.colors?.codes ?? []).reduce(
    (acc, curr) => {
      acc[curr.id] = {
        color: `#${curr.color}`,
        includeChildren: curr.includeChildren,
      }
      return acc
    },
    {},
  ))

  const userCodeColorsWithChildren = computed(() =>
    Object.keys(userCodeColors.value).reduce((codes, codeId) => {
      codes.push(Number(codeId))

      if (userCodeColors.value[codeId].includeChildren) {
        recursivelyAddChildren(codes, codeId)
      }

      return codes
    }, [] as TCodeId[]),
  )

  function getNewsItemColoredCodes(newsVersion): TCodeId[] {

    const intersection = $intersection(
      newsVersion.codes.map((item) => item.code.id),
      userCodeColorsWithChildren.value,
    )

    return intersection.reduce(
      (acc, codeId) => {

        if (userCodeColors.value[codeId]) {
          acc.finalItems.push(codeId)
          return acc
        }

        const parent: TCodeId | null = allCodes.value[codeId].track.reduce((acc, codeId) => {
          if (acc) {
            return acc
          } else if (userCodeColors.value[codeId]) {
            return codeId
          } else {
            return null
          }
        }, null as TCodeId | null)

        if (parent && acc.parents.includes(parent)) {
          return acc
        }

        acc.parents.push(parent)
        acc.finalItems.push(codeId)

        return acc
      },
      {
        parents: [],
        finalItems: [],
      } as {
        parents: (TCodeId | null)[]
        finalItems: TCodeId[]
      },
    ).finalItems
  }

  function setNewsCodesColorMap(newsIds: TNewsId[]) {

    if (!Object.keys(userCodeColors.value).length) {
      return
    }

    newsIds.forEach((id) => {
      const version = newsStore.news[id]?.version

      if (version && version.codes?.length) {
        const color = getNewsItemColoredCodes(version)
        if (color.length) {
          newsColoredCodes[id] = color
        }
      }
    })
  }

  return {
    newsColoredCodes,
    setNewsCodesColorMap,
  }
})
